import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer, toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Fuse from "fuse.js";
import { fetchAllBuilds } from "../../redux/actions/buildActions";
import { RootState, AppDispatch } from "../../redux/store";

const renderBooleanCell = (params: any) => (
  <p style={{ textAlign: "center", margin: 0 }}>{params.value ? "✔️" : "❌"}</p>
);

const BuildsTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { builds, loading, error } = useSelector(
    (state: RootState) => state.builds
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newBuildName, setNewBuildName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Fetch all builds when the component mounts
  useEffect(() => {
    dispatch(fetchAllBuilds());
  }, [dispatch]);

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    const nonDeletedBuilds = builds.filter((b) => !b.deleted_at);
    return new Fuse(nonDeletedBuilds, {
      keys: ["id", "build_name", "build_notes"],
      threshold: 0.5, // Lower = more strict matching
      includeScore: true,
    });
  }, [builds]);

  // Filter builds based on search query
  const filteredBuilds = useMemo(() => {
    const nonDeletedBuilds = builds.filter((b) => !b.deleted_at);

    if (!searchQuery.trim()) {
      return nonDeletedBuilds;
    }

    const results = fuse.search(searchQuery);
    return results.map((result) => result.item);
  }, [builds, searchQuery, fuse]);

  const handleViewBuild = useCallback(
    (id: string) => {
      navigate(`/dashboard/builds/${id}`);
    },
    [navigate]
  );

  const handleCreateBuild = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewBuildName("");
  };

  const handleCreateBuildSubmit = () => {
    if (!newBuildName) {
      toast.error("Build Name is required");
      return;
    }

    // Generate a UUID for the build ID
    // const buildId = uuidv4();

    // TODO: Implement proper build creation with platform entries
    // This is currently commented out because a build must have at least one valid platform
    // Will be implemented by Eli
    /*
    const buildData = {
      build_id: buildId,
      build_name: newBuildName,
      entries: [],
    };

    dispatch(upsertBuild(buildData))
      .unwrap()
      .then((build) => {
        toast.success("Build created successfully");
        handleDialogClose();
        navigate(`/dashboard/builds/${build.id}`, {
          state: { toastMessage: "Build created successfully" },
        });
      })
      .catch((error) => {
        toast.error(`Failed to create build: ${error.message}`);
      });
    */

    // Show a message instead of making the API call
    toast.info(
      `This will trigger a backend task to create a new build named "${newBuildName}" once Eli implements that functionality.`
    );

    // Close the dialog
    handleDialogClose();
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Build ID",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "build_name",
      headerName: "Build Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "build_notes",
      headerName: "Build Notes",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%"
        }}>
          {params.value || "—"}
        </div>
      ),
    },
    {
      field: "s3_build_is_hot",
      headerName: "Hot",
      flex: 0.3,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      renderCell: renderBooleanCell,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleViewBuild(params.row.id)}
        >
          View
        </Button>
      ),
      headerAlign: "center",
      align: "center",
    },
  ];

  if (loading && builds.length === 0) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />

      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}
      >
        <TextField
          placeholder="Search by Build ID, Build Name, or Notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 500 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" color="primary" onClick={handleCreateBuild}>
          Create Build
        </Button>
      </Box>

      <Box sx={{ height: "70vh", width: "100%" }}>
        <DataGrid
          rows={filteredBuilds}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "build_name", sort: "asc" }],
            },
          }}
        />
      </Box>

      {/* Dialog for creating a new build */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Create New Build</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter a name for the new build. A unique ID will be generated
            automatically.
          </Typography>
          <TextField
            label="Build Name"
            value={newBuildName}
            onChange={(e) => setNewBuildName(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="The build name in psimagespersona/avatarapps, without `.zip`."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleCreateBuildSubmit}
            color="primary"
            disabled={!newBuildName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BuildsTab;
